import { getIn, useFormikContext } from 'formik';
import { pluralize } from 'inflection';
import * as _ from 'lodash-es';
import React, { useEffect, useMemo, useState } from 'react';
import * as styles from './IntegrationSelectSetting.module.scss';
import IntegrationSelectSettingItem, {
  SELECT_OPTIONS,
} from './IntegrationSelectSettingItem';
import SearchableSelectByName from './SearchableSelectByName';
import BorderedIcon from '../../components/BorderedIcon';
import Button from '../../components/Button';
import { Field, FieldSet } from '../../components/Form';
import LoadingIndicator from '../../components/LoadingIndicator';
import SelectEntityOption from '../../components/SelectEntityOption';

export enum EnableOption {
  disabled = 'DISABLED',
  mirror = 'MIRROR',
  all = 'ALL',
  public = 'PUBLIC',
  custom = 'CUSTOM',
}

export const SETTING_OPTIONS = ['search', 'mentions', 'sync'];

interface IntegrationSelectSettingProps {
  enableOptions: EnableOption[];
  name: (typeof SETTING_OPTIONS)[number];
  itemIcon?: React.ReactNode;
  itemTerm: string;
  itemsLoading?: boolean;
  items: {
    id: string;
    name: string;
    subItems?: { id: string; name: string }[];
  }[];
  settingNameToMirror?: string | null;
}

const IntegrationSelectSetting = ({
  enableOptions,
  itemIcon,
  items,
  itemsLoading = false,
  itemTerm,
  settingNameToMirror = null,
  name,
  ...itemProps
}: IntegrationSelectSettingProps) => {
  const itemsTerm = pluralize(itemTerm);
  const orderedOptions = useMemo(
    () =>
      Object.values(EnableOption).filter((option) =>
        enableOptions.includes(option),
      ),
    [enableOptions],
  );
  const isCustomOnly = _.isEqual(orderedOptions, [EnableOption.custom]);
  const itemById = _.keyBy(items, 'id');
  const { values, setFieldValue } = useFormikContext();
  const { enable, selectedItems } = getIn(values, name);
  const [isAdding, setIsAdding] = useState(false);

  const isSimple = _.isEqual(
    [EnableOption.disabled, EnableOption.all],
    orderedOptions,
  );

  const labels = {
    [EnableOption.all]: isSimple ? `Enable ${name}` : `All ${itemsTerm}`,
    [EnableOption.mirror]: `Use the same ${itemsTerm} configured for ${_.startCase(
      settingNameToMirror ?? '',
    )}`,
    [EnableOption.public]: `All public ${itemsTerm}`,
    [EnableOption.custom]: `Selected ${itemsTerm}`,
    [EnableOption.disabled]: `Disable ${name}`,
  };

  const legend = isSimple ? null : `Choose a ${itemTerm}`;

  useEffect(() => {
    setIsAdding(false);
  }, [enable]);

  return (
    <>
      {!isCustomOnly && (
        <FieldSet legend={legend}>
          {orderedOptions.map((option) => (
            <Field
              key={option}
              name={`${name}.enable`}
              value={option}
              label={labels[option]}
              type="radio"
            />
          ))}
        </FieldSet>
      )}
      {enable === EnableOption.custom &&
        (itemsLoading ? (
          <LoadingIndicator />
        ) : (
          <>
            {Object.keys(selectedItems).map((id) => {
              const item = itemById[id];
              return (
                !!item && (
                  <div key={id} className={styles.section}>
                    <IntegrationSelectSettingItem
                      itemTerm={itemTerm}
                      itemIcon={itemIcon}
                      item={item}
                      name={`${name}.selectedItems.${id}`}
                      {...itemProps}
                    />
                  </div>
                )
              );
            })}
            <div className={styles.section}>
              {isAdding ? (
                <SearchableSelectByName
                  items={items}
                  renderItem={(item) => (
                    <SelectEntityOption
                      icon={<BorderedIcon>{itemIcon}</BorderedIcon>}
                      label={item.name}
                      disabled={Object.keys(selectedItems).includes(item.id)}
                    />
                  )}
                  placeholder={`Search ${itemsTerm}…`}
                  onSelect={(item) => {
                    if (!Object.keys(selectedItems).includes(item.id)) {
                      setFieldValue(`${name}.selectedItems.${item.id}`, {
                        select: SELECT_OPTIONS.all,
                        ids: new Set(),
                      });
                      setIsAdding(false);
                    }
                  }}
                  onClose={() => setIsAdding(false)}
                />
              ) : (
                <Button onClick={() => setIsAdding(true)} variant="secondary">
                  Add {itemTerm}
                </Button>
              )}
            </div>
          </>
        ))}
    </>
  );
};

export default IntegrationSelectSetting;
